import React, { useEffect, useState } from "react";
import i18next from "i18next";
import {
  Button,
  Input,
  Text,
  Alert,
  Field,
  Stack,
  Card,
  RadioGroup,
  HStack,
} from "@chakra-ui/react";
import { useFormik } from "formik";
import { observer } from "mobx-react";
import { useNavigate } from "react-router-dom";
import { appRoutes } from "../../../../App";
import { t } from "i18next";
import { Survey, UpdateSurvey } from "../../models/Survey";
import { CreateOrUpdateSurveySchema } from "./CreateOrUpdateSurveySchema";
import { useStores } from "../../../shared/stores/RootStore";
import { CustomDialog } from "../../../shared/views/CustomDialog";
import { DistanceUnit } from "@keita/lanepatrol-sdk";
import { SearchBox } from "@mapbox/search-js-react";
import { MAPBOX_KEY } from "../../../../config";
import { City, CreateOrUpdateCity } from "../../models/City";
import { CityApi } from "../../../map/services/CityApi";

const initialValues: UpdateSurvey = {
  cityId: "",
  name: "",
};

const CreateOrUpdateSurveyBase: React.FC<{
  onClose: () => void;
  survey?: Survey;
}> = ({ survey, onClose }) => {
  const { surveyStore, accountStore } = useStores();
  const navigate = useNavigate();
  const account = accountStore.selectedUserAccount!;

  const [submitting, setSubmitting] = useState(false);
  const [city, _setCity] = useState(survey?.city);
  const [newCity, setNewCity] = useState<CreateOrUpdateCity>();
  const setCity = (city?: City) => {
    formik.setFieldValue("cityId", city ? city.id : "");
    _setCity(city);
  };

  const searchPlace = async (
    name: string,
    country: string,
    lngLat: GeoJSON.Position,
  ) => {
    const response = await CityApi.search(name, country);
    const cities = response.data;
    console.log("Country:", country);

    if (cities.length) {
      setCity(cities[0]);
    } else {
      var rightHandTraffic = true;
      var distanceUnit = DistanceUnit.Metric;

      setNewCity({
        name,
        country,
        lng: lngLat[0],
        lat: lngLat[1],
        rightHandTraffic,
        distanceUnit,
      });
    }
  };

  const formik = useFormik({
    initialValues: survey
      ? {
          name: survey.name,
          cityId: survey.city.id,
        }
      : initialValues,

    onSubmit: async (values: UpdateSurvey) => {
      setSubmitting(true);
      if (!survey) {
        await surveyStore
          .addSurvey({
            accountId: account.accountId,
            name: values.name,
            cityId: values.cityId,
          })
          .then((survey) => {
            navigate(appRoutes.survey(survey.id));
          });
      } else {
        await surveyStore.updateSurvey(
          {
            name: values.name,
            cityId: values.cityId,
          },
          survey.id!,
        );
      }
      setSubmitting(false);
      formik.setValues(formik.initialValues);
      onClose();
    },

    validationSchema: CreateOrUpdateSurveySchema,
  });

  useEffect(() => {
    formik.setValues(formik.initialValues);
    // eslint-disable-next-line
  }, []);

  return (
    <CustomDialog
      header={
        !survey
          ? `${i18next.t("shared:survey.modal.titleNew")}`
          : `${i18next.t("shared:survey.modal.title")}`
      }
      footer={
        <Button
          colorPalette="primary"
          type="submit"
          form="createOrUpdateSurvey"
          disabled={!formik.isValid || !formik.dirty}
          loading={submitting}
        >
          {`${i18next.t("shared:survey.submit.done")}`}
        </Button>
      }
      onClose={onClose}
    >
      <form id="createOrUpdateSurvey" onSubmit={formik.handleSubmit}>
        <Stack gap={4}>
          <Field.Root invalid={!!formik.errors.name} required>
            <Field.Label>
              {`${i18next.t("shared:survey.modal.survey.title")}`}
            </Field.Label>
            <Input
              required
              placeholder={`${i18next.t(
                "shared:survey.modal.survey.placeholder",
              )}`}
              name="name"
              onChange={formik.handleChange}
              value={formik.values.name}
            />
            <Field.ErrorText>{formik.errors.name}</Field.ErrorText>
          </Field.Root>

          <Field.Root>
            <Field.Label>City</Field.Label>
            <Stack w="full">
              {city ? (
                <Card.Root
                  p={3}
                  onClick={() => setCity()}
                  cursor={"pointer"}
                  _hover={{ bgColor: "gray.50", textDecor: "line-through" }}
                >
                  <Text>
                    {city.name}, {city.country}
                  </Text>
                </Card.Root>
              ) : newCity ? (
                <Stack>
                  <Card.Root
                    p={3}
                    onClick={() => setNewCity(undefined)}
                    cursor={"pointer"}
                    _hover={{ bgColor: "gray.50", textDecor: "line-through" }}
                  >
                    <Text>
                      {newCity.name}, {newCity.country}
                    </Text>
                  </Card.Root>

                  <Field.Root>
                    <Field.Label>Distance unit</Field.Label>
                    <RadioGroup.Root
                      name="distanceUnit"
                      colorPalette={"primary"}
                      value={newCity.distanceUnit}
                      onValueChange={({ value }) => {
                        const distanceUnit = value as DistanceUnit;
                        setNewCity((c) => ({ ...c!, distanceUnit }));
                      }}
                    >
                      <HStack gap="6">
                        <RadioGroup.Item value={DistanceUnit.Metric}>
                          <RadioGroup.ItemHiddenInput />
                          <RadioGroup.ItemIndicator />
                          <RadioGroup.ItemText>Metric</RadioGroup.ItemText>
                        </RadioGroup.Item>

                        <RadioGroup.Item value={DistanceUnit.Imperial}>
                          <RadioGroup.ItemHiddenInput />
                          <RadioGroup.ItemIndicator />
                          <RadioGroup.ItemText>Imperial</RadioGroup.ItemText>
                        </RadioGroup.Item>
                      </HStack>
                    </RadioGroup.Root>
                  </Field.Root>

                  <Field.Root>
                    <Field.Label>Drive on</Field.Label>
                    <RadioGroup.Root
                      colorPalette={"primary"}
                      value={newCity.rightHandTraffic ? "right" : "left"}
                      onValueChange={({ value }) => {
                        const rightHandTraffic = value === "right";
                        setNewCity((c) => ({ ...c!, rightHandTraffic }));
                      }}
                    >
                      <HStack gap="6">
                        <RadioGroup.Item value="left">
                          <RadioGroup.ItemHiddenInput />
                          <RadioGroup.ItemIndicator />
                          <RadioGroup.ItemText>Left side</RadioGroup.ItemText>
                        </RadioGroup.Item>

                        <RadioGroup.Item value="right">
                          <RadioGroup.ItemHiddenInput />
                          <RadioGroup.ItemIndicator />
                          <RadioGroup.ItemText>Right side</RadioGroup.ItemText>
                        </RadioGroup.Item>
                      </HStack>
                    </RadioGroup.Root>
                  </Field.Root>

                  <HStack justify={"end"}>
                    <Button
                      loading={submitting}
                      size="sm"
                      colorPalette={"primary"}
                      onClick={() => {
                        setSubmitting(true);
                        CityApi.create(newCity).then(({ data }) => {
                          setCity(data);
                          setNewCity(undefined);
                          setSubmitting(false);
                        });
                      }}
                    >
                      Add city
                    </Button>
                  </HStack>
                </Stack>
              ) : (
                <Stack minHeight={"300px"}>
                  <SearchBox
                    accessToken={MAPBOX_KEY}
                    options={{ language: "en", types: "place" }}
                    onRetrieve={(result) => {
                      const { coordinates } = result.features[0].geometry;
                      const { name, context } = result.features[0].properties;
                      const country = context.country?.name ?? "";

                      searchPlace(name, country, coordinates);
                    }}
                  />
                </Stack>
              )}
            </Stack>
          </Field.Root>
        </Stack>
      </form>

      {!survey && (
        <Alert.Root status="info" mt={6}>
          <Alert.Indicator />
          <Alert.Content>
            <div>
              <Text fontSize="sm" fontWeight="semibold">
                {t("shared:survey.modal.survey.accountAlert")}
              </Text>
              <Text>{account.name}</Text>
            </div>
          </Alert.Content>
        </Alert.Root>
      )}
    </CustomDialog>
  );
};

export const CreateOrUpdateSurveyModal = observer(CreateOrUpdateSurveyBase);
